//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import FocusTrap from 'focus-trap-react';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
// import clientStyles from '@style/common/client.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Modal, Button, ButtonStyle, Input } from '@cmp/common';
//------------------------------------------------------------------------------
// Classes ---------------------------------------------------------------------
import FormValidator from '@classes/formValidator';
//------------------------------------------------------------------------------
// Helpers ---------------------------------------------------------------------
import { sendProfessionalCode } from '@helpers/referrals';
import { responseFormatter } from '@helpers/formatter';
import {
  isNameValid,
  isEmailValid,
  isPhoneNumberValid,
} from '@helpers/validation';
import { withTranslation } from 'react-i18next';
import { StoreConfigContext } from '@helpers/storeConfigContext';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class ProfessionalCode extends React.Component {
  static contextType = StoreConfigContext;
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      email: '',
      phoneNumber: '',
      loading: false,
      errorMessage: null,
      countryCode: '',
    };

    this.onSendClick = this.onSendClick.bind(this);
    this.setupValidator();
    this.mountTrap = this.mountTrap.bind(this);
    this.unmountTrap = this.unmountTrap.bind(this);
  }

  componentDidMount() {
    this.setupData();
    this.mountTrap();
  }
  mountTrap() {
    this.setState({ activeTrap: true });
  }

  unmountTrap() {
    this.setState({ activeTrap: false });
  }

  setupData() {
    const { location: { state } = {} } = this.props;

    const { fromReferral } = state || {};
    if (!fromReferral) return;

    const {
      consumerEmail: email = '',
      consumerName: name = '',
      consumerPhone: phoneNumber = '',
    } = fromReferral;
    this.setState({ email, name, phoneNumber });
  }

  setupValidator() {
    const { storeConfig = {} } = this.context || {};
    const { countryCode = '' } = storeConfig;

    // to-do: create exclusivity rules in FormValidator
    const items = [
      {
        key: 'name',
        defaultValue: '',
        method: (string) => isNameValid(string),
        validWhen: true,
      },
      {
        key: 'email',
        defaultValue: '',
        method: (string, data) => {
          if (!data.phoneNumber || !data.email) {
            return (
              isEmailValid(string) ||
              isPhoneNumberValid(data.phoneNumber, countryCode)
            );
          }

          return (
            isEmailValid(string) &&
            isPhoneNumberValid(data.phoneNumber, countryCode)
          );
        },
        validWhen: true,
      },
      {
        key: 'phoneNumber',
        defaultValue: '',
        method: (string, data) => {
          if (!data.phoneNumber || !data.email) {
            return (
              isPhoneNumberValid(string, countryCode) ||
              isEmailValid(data.email)
            );
          }

          return (
            isPhoneNumberValid(string, countryCode) && isEmailValid(data.email)
          );
        },
        validWhen: true,
      },
    ];

    this.validator = new FormValidator(items);
  }

  headerComponent() {
    const { t } = this.props;
    return (
      <div className={styles.Header}>
        <h2 className={styles.Title}>{t('dashboard_ParentDetails')}</h2>
        <span className={styles.Subtitle}>
          {t('dashboard_ParentDetailsForReferral')}
        </span>
      </div>
    );
  }

  onSendClick() {
    const that = this;
    const { name, email, phoneNumber } = this.state;
    const { history, location } = this.props;
    const {
      featureFlags: { professionalCodeSMS },
    } = this.context;

    const data = { name, email };
    if (professionalCodeSMS) {
      data.phoneNumber = phoneNumber;
    }

    this.setState({ loading: true });

    sendProfessionalCode(data)
      .then(() => {
        history.push({
          pathname: '/recommendation/professional-code-success',
          state: {
            user: data,
            from: location.state.from,
            modal: true,
          },
        });
      })
      .catch((err) =>
        that.setState({
          errorMessage: responseFormatter.error(err).message(),
        })
      )
      .finally(() => that.setState({ loading: false }));
  }

  formComponent() {
    const {
      name,
      email,
      phoneNumber,
      loading,
      errorMessage,
      countryCodeFromState,
    } = this.state;
    const { t } = this.props;
    const {
      featureFlags: { professionalCodeSMS },
      storeConfig: { countryCode },
    } = this.context;

    if (!countryCodeFromState) this.setupValidator();

    const validateAll = this.validator.validateAll(this.state);
    const ParentNameAuxText = professionalCodeSMS
      ? 'dashboard_ParentNameAux'
      : 'dashboard_ParentNameAuxNoSMS';

    const showPhoneInvalid =
      professionalCodeSMS &&
      phoneNumber &&
      !isPhoneNumberValid(phoneNumber, countryCode);
    return (
      <div className={styles.Form}>
        <Input
          title={t('dashboard_ParentName')}
          placeholder={t('dashboard_ParentNamePH')}
          className={styles.Input}
          value={name}
          onChange={(name) => this.setState({ name })}
          id="focusedInput"
        />
        <span className={styles.Auxiliar}>{t(ParentNameAuxText)}</span>
        <Input
          title={t('dashboard_Email')}
          placeholder={t('dashboard_EmailPH')}
          className={styles.Input}
          value={email}
          onChange={(email) => this.setState({ email })}
        />
        {professionalCodeSMS && (
          <Input
            title={t('dashboard_Phone')}
            placeholder={t('dashboard_PhonePH')}
            className={styles.Input}
            value={phoneNumber}
            onChange={(phoneNumber) => this.setState({ phoneNumber })}
          />
        )}
        {showPhoneInvalid && (
          <span className={styles.AuxiliarError}>
            {t('dashboard_ParentPhoneError')}
          </span>
        )}
        <span className={styles.Disclosure}>
          {t('dashboard_ConfirmingConsent')}
        </span>
        <div className={styles.SubmitArea}>
          <Button
            style={ButtonStyle.Primary}
            className={styles.Button}
            onClick={this.onSendClick}
            loading={loading}
            disabled={!validateAll.isValid}
          >
            {t('dashboard_SendReferralCode')}
          </Button>
        </div>
        {errorMessage && (
          <span className={styles.ErrorMessage}>{errorMessage}</span>
        )}
      </div>
    );
  }

  render() {
    const { history, location, t } = this.props;
    const componentClasses = classNames(styles.Parent);

    return (
      <FocusTrap
        focusTrapOptions={{
          initialFocus: '#focusedInput',
        }}
      >
        <Modal.Base
          className={componentClasses}
          onClose={() => history.push(location.state.from)}
        >
          <Helmet>
            <title>{t('dashboard_ParentDetails')}</title>
          </Helmet>

          {this.headerComponent()}
          {this.formComponent()}
        </Modal.Base>
      </FocusTrap>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(ProfessionalCode);

ProfessionalCode.contextType = StoreConfigContext;
